import { lazy, Suspense, type ReactNode } from 'react';
import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  Outlet,
} from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

// Eager loaded - critical path
import LandingPage from './pages/LandingPage';
import PublicDocsPage from './pages/PublicDocsPage';
import { LoginForm } from './features/auth/components/LoginForm';
import { MagicLinkVerifyPage } from './features/auth/components/MagicLinkVerifyPage';
import { RegisterForm } from './features/auth/components/RegisterForm';
import { ForgotPasswordForm } from './features/auth/components/ForgotPasswordForm';
import { ResetPasswordForm } from './features/auth/components/ResetPasswordForm';
import { DashboardLayout } from './layouts/dashboard/DashboardLayout';
import { WorkspaceChoiceGate } from './features/crm/business-units/components/WorkspaceChoiceGate';
import { SettingsLayout } from './layouts/settings/SettingsLayout';
import { CrmSettingsLayout } from './layouts/crm-settings/CrmSettingsLayout';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ProtectedRoute } from './features/auth/components/ProtectedRoute';
import { GuestRoute } from './features/auth/components/GuestRoute';
import { OrganizationSettingsRedirect } from './pages/organization/OrganizationSettingsRedirect';
import { AccessDeniedPage } from './pages/errors/AccessDeniedPage';
import { NotFoundRouteComponent, UnauthorizedRouteComponent } from './router-error-components';
import {
  OVERTIME_PAGE_ACCESS_PERMISSIONS,
  REGULARIZATION_PAGE_ACCESS_PERMISSIONS,
} from './features/attendance/permission-contracts';

// =============================================================================
// ROUTE PROTECTION HELPERS
// =============================================================================

interface ProtectionOptions {
  // Single permission (ABAC format: module:resource:action:scope)
  requiredPermission?: string;
  // Multiple permissions (AND by default)
  requiredPermissions?: string[];
  // Module-level access (any permission in module)
  requiredModule?: string;
  // Role-based access (for admin routes)
  requiredRole?: string | string[];
  // OR logic for multiple permissions
  requireAny?: boolean;
}

const dashboardAccessDeniedFallback = (
  <DashboardLayout>
    <AccessDeniedPage />
  </DashboardLayout>
);

const settingsAccessDeniedFallback = (
  <DashboardLayout>
    <SettingsLayout>
      <AccessDeniedPage />
    </SettingsLayout>
  </DashboardLayout>
);

const crmSettingsAccessDeniedFallback = (
  <DashboardLayout>
    <CrmSettingsLayout>
      <AccessDeniedPage />
    </CrmSettingsLayout>
  </DashboardLayout>
);

// The workspace-choice gate sits ABOVE the dashboard shell: a multi-unit CRM
// member who hasn't picked a workspace this login session is sent to the
// chooser before the sidebar/header ever paint — whether they landed on
// /dashboard after signing in or deep-linked straight into a CRM page.
function withProtection(children: ReactNode, options: ProtectionOptions = {}) {
  return (
    <ProtectedRoute {...options} fallback={dashboardAccessDeniedFallback}>
      <WorkspaceChoiceGate>
        <DashboardLayout>{children}</DashboardLayout>
      </WorkspaceChoiceGate>
    </ProtectedRoute>
  );
}

function withSettingsProtection(children: ReactNode, options: ProtectionOptions = {}) {
  return (
    <ProtectedRoute {...options} fallback={settingsAccessDeniedFallback}>
      <WorkspaceChoiceGate>
        <DashboardLayout>
          <SettingsLayout>{children}</SettingsLayout>
        </DashboardLayout>
      </WorkspaceChoiceGate>
    </ProtectedRoute>
  );
}

function withCrmSettingsProtection(children: ReactNode, options: ProtectionOptions = {}) {
  return (
    <ProtectedRoute {...options} fallback={crmSettingsAccessDeniedFallback}>
      <WorkspaceChoiceGate>
        <DashboardLayout>
          <CrmSettingsLayout>{children}</CrmSettingsLayout>
        </DashboardLayout>
      </WorkspaceChoiceGate>
    </ProtectedRoute>
  );
}

// Chrome-free protection for full-screen interstitials (the workspace chooser):
// authenticated + permission-checked, but no dashboard shell around it.
function withBareProtection(children: ReactNode, options: ProtectionOptions = {}) {
  return (
    <ProtectedRoute {...options} fallback={dashboardAccessDeniedFallback}>
      {children}
    </ProtectedRoute>
  );
}

type LoginRouteSearch = {
  email?: string;
  tenantSlug?: string;
  verify?: boolean;
};

type ResetPasswordRouteSearch = {
  token?: string;
};

type MagicLinkVerifyRouteSearch = {
  token?: string;
};

function getSearchString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function isEnabledSearchFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === '1' || value === '"1"' || value === 1;
}

function parseLoginSearch(search: Record<string, unknown>): LoginRouteSearch {
  const loginSearch: LoginRouteSearch = {};
  const email = getSearchString(search.email);
  const tenantSlug = getSearchString(search.tenantSlug);

  if (email) {
    loginSearch.email = email;
  }

  if (tenantSlug) {
    loginSearch.tenantSlug = tenantSlug;
  }

  if (isEnabledSearchFlag(search.verify)) {
    loginSearch.verify = true;
  }

  return loginSearch;
}

function parseResetPasswordSearch(search: Record<string, unknown>): ResetPasswordRouteSearch {
  const token = getSearchString(search.token);
  return token ? { token } : {};
}

function parseMagicLinkVerifySearch(search: Record<string, unknown>): MagicLinkVerifyRouteSearch {
  const token = getSearchString(search.token);
  return token ? { token } : {};
}

// Lazy loaded - Profile
const ProfilePage = lazy(() =>
  import('./pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);

// Lazy loaded pages - HRMS core
const EmployeesPage = lazy(() =>
  import('./pages/employees/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);
const MyTeamPage = lazy(() =>
  import('./pages/employees/MyTeamPage').then((m) => ({ default: m.MyTeamPage })),
);
const EmployeeCreatePage = lazy(() =>
  import('./pages/employees/EmployeeCreatePage').then((m) => ({
    default: m.EmployeeCreatePage,
  })),
);
const LegalEntitiesPage = lazy(() =>
  import('./features/hrms/legal-entities').then((m) => ({ default: m.LegalEntitiesPage })),
);
const OnboardingPage = lazy(() =>
  import('./features/hrms/onboarding').then((m) => ({ default: m.OnboardingPage })),
);
const OnboardingProgramTemplatesPage = lazy(() =>
  import('./features/hrms/onboarding').then((m) => ({
    default: m.OnboardingProgramTemplatesPage,
  })),
);
const DocumentTemplatesPage = lazy(() =>
  import('./features/hrms/onboarding').then((m) => ({ default: m.DocumentTemplatesPage })),
);
const OffboardingPage = lazy(() =>
  import('./features/hrms/offboarding').then((m) => ({ default: m.OffboardingPage })),
);
const EmployeeDetailPage = lazy(() =>
  import('./pages/employees/EmployeeDetailPage').then((m) => ({ default: m.EmployeeDetailPage })),
);
const MyProfileRedirect = lazy(() =>
  import('./pages/employees/MyProfileRedirect').then((m) => ({ default: m.MyProfileRedirect })),
);
const LeavesPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeavesPage })),
);
const PayrollRunsPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.PayrollRunsPage })),
);
const PayrollRunDetailPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.PayrollRunDetailPage })),
);
const PayrollAccountingMappingsPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({
    default: m.PayrollAccountingMappingsPage,
  })),
);
const PayrollStatutoryReportsPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({
    default: m.PayrollStatutoryReportsPage,
  })),
);
const PayslipDetailPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.PayslipDetailPage })),
);
const MyPayslipsPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.MyPayslipsPage })),
);
const StatutoryConfigPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.StatutoryConfigPage })),
);
const SettlementPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.SettlementPage })),
);
const TaxDeclarationPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.TaxDeclarationPage })),
);
const SalaryComponentsPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.SalaryComponentsPage })),
);
const SalaryStructuresPage = lazy(() =>
  import('./features/hrms/payroll').then((m) => ({ default: m.SalaryStructuresPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);

// Lazy loaded - Unified Settings pages
const PreferencesPage = lazy(() =>
  import('./pages/settings/PreferencesPage').then((m) => ({ default: m.PreferencesPage })),
);
const NotificationPreferencesPage = lazy(() =>
  import('./pages/settings/NotificationPreferencesPage').then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);

// Lazy loaded - Organization sub-pages (for unified settings)
const OrgProfilePage = lazy(() =>
  import('./pages/organization/OrgProfilePage').then((m) => ({ default: m.OrgProfilePage })),
);
const OrgRegionalPage = lazy(() =>
  import('./pages/organization/OrgRegionalPage').then((m) => ({ default: m.OrgRegionalPage })),
);
const OrgDepartmentsPage = lazy(() =>
  import('./pages/organization/OrgDepartmentsPage').then((m) => ({
    default: m.OrgDepartmentsPage,
  })),
);
const OrgTeamsPage = lazy(() =>
  import('./pages/organization/OrgTeamsPage').then((m) => ({ default: m.OrgTeamsPage })),
);

// Lazy loaded - HRMS Departments
const DepartmentsPage = lazy(() =>
  import('./features/hrms/departments').then((m) => ({ default: m.DepartmentsPage })),
);
const DepartmentDetailPage = lazy(() =>
  import('./features/hrms/departments').then((m) => ({ default: m.DepartmentDetailPage })),
);
const TeamsPage = lazy(() =>
  import('./pages/teams/TeamsPage').then((m) => ({ default: m.TeamsPage })),
);

// Lazy loaded - HRMS Designations
const DesignationsPage = lazy(() =>
  import('./features/hrms/designations').then((m) => ({ default: m.DesignationsPage })),
);

// Lazy loaded - HRMS Positions
const PositionsPage = lazy(() =>
  import('./features/hrms/positions').then((m) => ({ default: m.PositionsPage })),
);

// Lazy loaded - HRMS Locations
const LocationsPage = lazy(() =>
  import('./features/hrms/locations').then((m) => ({ default: m.LocationsPage })),
);

// Lazy loaded - HRMS Leave Management
const LeaveRequestsPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveRequestsPage })),
);
const LeaveCalendarPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveCalendarPage })),
);
const LeaveBalancePage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveBalancePage })),
);
const LeaveTypesPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveTypesPage })),
);
const LeaveTransactionHistoryPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveTransactionHistoryPage })),
);
const LeaveEncashmentPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveEncashmentPage })),
);
const DelegationManagementPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.DelegationManagementPage })),
);
const LeaveAdminPage = lazy(() =>
  import('./features/hrms/leaves').then((m) => ({ default: m.LeaveAdminPage })),
);

// Lazy loaded - HRMS Holidays
const HolidayCalendarPage = lazy(() =>
  import('./features/hrms/holidays').then((m) => ({ default: m.HolidayCalendarPage })),
);
const HolidayPolicyPage = lazy(() =>
  import('./features/hrms/holidays').then((m) => ({ default: m.HolidayPolicyPage })),
);
const MyHolidaysPage = lazy(() =>
  import('./features/hrms/holidays').then((m) => ({ default: m.MyHolidaysPage })),
);
const FloatingHolidayApprovalsPage = lazy(() =>
  import('./features/hrms/holidays').then((m) => ({ default: m.FloatingHolidayApprovalsPage })),
);

// Lazy loaded - HRMS Shifts
const ShiftManagementPage = lazy(() =>
  import('./features/hrms/shifts').then((m) => ({ default: m.ShiftManagementPage })),
);
const ShiftRosterPage = lazy(() =>
  import('./features/hrms/shifts').then((m) => ({ default: m.ShiftRosterPage })),
);
const ShiftSwapPage = lazy(() =>
  import('./features/hrms/shifts').then((m) => ({ default: m.ShiftSwapPage })),
);
const DayRosterPage = lazy(() =>
  import('./features/hrms/shifts').then((m) => ({ default: m.DayRosterPage })),
);
const ShiftChangeRequestPage = lazy(() =>
  import('./features/hrms/shifts').then((m) => ({ default: m.ShiftChangeRequestPage })),
);

// Lazy loaded - HRMS Skills
const SkillCatalogPage = lazy(() =>
  import('./features/hrms/skills').then((m) => ({ default: m.SkillCatalogPage })),
);

// Lazy loaded - HRMS Assets (unified)
const AssetsPage = lazy(() =>
  import('./pages/assets/AssetsPage').then((m) => ({ default: m.AssetsPage })),
);

// Lazy loaded - HRMS Benefits
const BenefitsPage = lazy(() =>
  import('./features/hrms/benefits').then((m) => ({ default: m.BenefitsPage })),
);
const MyBenefitsPage = lazy(() =>
  import('./features/hrms/benefits').then((m) => ({ default: m.MyBenefitsPage })),
);
const BenefitPlansPage = lazy(() =>
  import('./features/hrms/benefits').then((m) => ({ default: m.BenefitPlansPage })),
);
const EnrollmentsPage = lazy(() =>
  import('./features/hrms/benefits').then((m) => ({ default: m.EnrollmentsPage })),
);
const ClaimsPage = lazy(() =>
  import('./features/hrms/benefits').then((m) => ({ default: m.ClaimsPage })),
);

// Lazy loaded - Attendance Module
const AttendanceDashboardPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.AttendanceDashboardPage })),
);
const TimesheetPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.TimesheetPage })),
);
const TimesheetApprovalsPage = lazy(() =>
  import('./features/attendance/pages/TimesheetApprovalsPage').then((m) => ({
    default: m.TimesheetApprovalsPage,
  })),
);
const RegularizationPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.RegularizationPage })),
);
const ViolationDashboardPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.ViolationDashboardPage })),
);
const TeamAttendancePage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.TeamAttendancePage })),
);
const AnalyticsPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.AnalyticsPage })),
);
const DeviceEventsPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.DeviceEventsPage })),
);
const PolicyListPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.PolicyListPage })),
);
const PolicyFormPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.PolicyFormPage })),
);
const OvertimePage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.OvertimePage })),
);
const ApprovalWorkflowsPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.ApprovalWorkflowsPage })),
);
const AttendanceImportsPage = lazy(() =>
  import('./features/attendance').then((m) => ({ default: m.AttendanceImportsPage })),
);

// Lazy loaded - Notifications
const NotificationsPage = lazy(() =>
  import('./features/notifications').then((m) => ({ default: m.NotificationsPage })),
);

// Lazy loaded - Tenant/Settings
const PlanSettingsPage = lazy(() =>
  import('./features/tenant').then((m) => ({ default: m.PlanSettingsPage })),
);

// Lazy loaded - Admin/Superadmin
const TenantsListPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.TenantsListPage })),
);
const TenantDetailPage = lazy(() =>
  import('./features/admin/pages/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage })),
);
const TenantConfigurationPage = lazy(() =>
  import('./features/admin/pages/TenantConfigurationPage').then((m) => ({
    default: m.TenantConfigurationPage,
  })),
);
const FeatureFlagsPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.FeatureFlagsPage })),
);
const AuditLogsPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.AuditLogsPage })),
);
const SystemConfigPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.SystemConfigPage })),
);
const ZeptoPlatformConfigPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.ZeptoPlatformConfigPage })),
);
const PlatformOperationsPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.PlatformOperationsPage })),
);
const PlanCatalogPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.PlanCatalogPage })),
);
const EventGovernancePage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.EventGovernancePage })),
);
const RolesListPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.RolesListPage })),
);
const TenantUsersPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.TenantUsersPage })),
);
const AuthSettingsPage = lazy(() =>
  import('./features/admin').then((m) => ({ default: m.AuthSettingsPage })),
);

// Lazy loaded - Recruitment
const CandidatesPage = lazy(() =>
  import('./features/hrms/recruitment').then((m) => ({ default: m.CandidatesPage })),
);
const JobPostingsPage = lazy(() =>
  import('./features/hrms/recruitment').then((m) => ({ default: m.JobPostingsPage })),
);

// Lazy loaded - CRM
const ChooseWorkspacePage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ChooseWorkspacePage })),
);
const ControlTowerPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ControlTowerPage })),
);
const CrmEntryGate = lazy(() => import('./pages/crm').then((m) => ({ default: m.CrmEntryGate })));
const LeadsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.LeadsPage })));
const LeadDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.LeadDetailPage })),
);
const ContactsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.ContactsPage })));
const ContactDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ContactDetailPage })),
);
const AccountsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.AccountsPage })));
const AccountDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.AccountDetailPage })),
);
const DealsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.DealsPage })));
const DealDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.DealDetailPage })),
);
const PipelineKanban = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PipelineKanban })),
);
const TasksPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.TasksPage })));
const TaskDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.TaskDetailPage })),
);
const ProductsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.ProductsPage })));
const ProductFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ProductFormPage })),
);
const ProjectsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.ProjectsPage })));
const ProjectDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ProjectDetailPage })),
);
const CampaignsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.CampaignsPage })));
const CampaignFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CampaignFormPage })),
);
const SalesAnalyticsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SalesAnalyticsPage })),
);
const CRMReportsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CRMReportsPage })),
);
const WorkflowAutomationPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.WorkflowAutomationPage })),
);
const WorkflowEditorPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.WorkflowEditorPage })),
);
const JourneyListPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.JourneyListPage })),
);
const JourneyBuilderPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.JourneyBuilderPage })),
);
const NotificationsCenterPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.NotificationsCenterPage })),
);
const EmailTemplatesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.EmailTemplatesPage })),
);
const PipelineSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PipelineSettingsPage })),
);
const ArchiveSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ArchiveSettingsPage })),
);
const ArchiveBrowserPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ArchiveBrowserPage })),
);
const LeadScoringSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.LeadScoringSettingsPage })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.IntegrationsPage })),
);
const WebhookSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.WebhookSettingsPage })),
);
const ApprovalsInboxPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ApprovalsInboxPage })),
);
const PartnersPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.PartnersPage })));
const PortalLoginPage = lazy(() =>
  import('./pages/portal').then((m) => ({ default: m.PortalLoginPage })),
);
const PortalHomePage = lazy(() =>
  import('./pages/portal').then((m) => ({ default: m.PortalHomePage })),
);
const VendorsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.VendorsPage })));
const VendorDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.VendorDetailPage })),
);
const VendorFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.VendorFormPage })),
);
const PurchaseOrdersPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PurchaseOrdersPage })),
);
const PurchaseOrderDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PurchaseOrderDetailPage })),
);
const PurchaseOrderFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PurchaseOrderFormPage })),
);
const QuotesPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.QuotesPage })));
const QuoteDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.QuoteDetailPage })),
);
const QuoteFormPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.QuoteFormPage })));
const CasesPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.CasesPage })));
const CaseDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CaseDetailPage })),
);
const CaseFormPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.CaseFormPage })));
const CampaignDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CampaignDetailPage })),
);
const DuplicatesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.DuplicatesPage })),
);
const ContractsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.ContractsPage })));
const ContractDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ContractDetailPage })),
);
const ContractFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ContractFormPage })),
);
const ActivitiesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ActivitiesPage })),
);
const CalendarPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.CalendarPage })));
const SalesOrdersPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SalesOrdersPage })),
);
const SalesOrderDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SalesOrderDetailPage })),
);
const SalesOrderFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SalesOrderFormPage })),
);
const InvoicesPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.InvoicesPage })));
const InvoiceDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.InvoiceDetailPage })),
);
const InvoiceFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.InvoiceFormPage })),
);
const PaymentsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.PaymentsPage })));
const PaymentDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PaymentDetailPage })),
);
const RecordPaymentPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.RecordPaymentPage })),
);
const CreditNotesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CreditNotesPage })),
);
const CreditNoteDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CreditNoteDetailPage })),
);
const CreditNoteFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CreditNoteFormPage })),
);
const PaymentTermsSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PaymentTermsSettingsPage })),
);
const PaymentMethodsSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PaymentMethodsSettingsPage })),
);
const TaxSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.TaxSettingsPage })),
);
const ExchangeRatesSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ExchangeRatesSettingsPage })),
);
const PriceBooksSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PriceBooksSettingsPage })),
);
const ApprovalWorkflowSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ApprovalWorkflowSettingsPage })),
);
const IntakeFormsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.IntakeFormsPage })),
);
const SLASettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SLASettingsPage })),
);
const GeneralSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.GeneralSettingsPage })),
);
const CustomFieldsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CustomFieldsPage })),
);
const InventoryStatusesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.InventoryStatusesPage })),
);
const ChargesSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.ChargesSettingsPage })),
);
const PaymentPlansSettingsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.PaymentPlansSettingsPage })),
);
const BusinessLinesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.BusinessLinesPage })),
);
const BusinessUnitsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.BusinessUnitsPage })),
);
const ForecastsPage = lazy(() => import('./pages/crm').then((m) => ({ default: m.ForecastsPage })));
const TerritoriesPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.TerritoriesPage })),
);
const TerritoryFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.TerritoryFormPage })),
);
const TerritoryDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.TerritoryDetailPage })),
);
const CompetitorsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CompetitorsPage })),
);
const CompetitorAnalyticsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.CompetitorAnalyticsPage })),
);
const SiteVisitsPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SiteVisitsPage })),
);
const SiteVisitDetailPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SiteVisitDetailPage })),
);
const SiteVisitFormPage = lazy(() =>
  import('./pages/crm').then((m) => ({ default: m.SiteVisitFormPage })),
);

// Lazy loaded - SuperAdmin (legacy)
const TenantsOverviewPage = lazy(() => import('./pages/superadmin/TenantsOverviewPage'));
const TenantAuthConfigPage = lazy(() => import('./pages/superadmin/TenantAuthConfigPage'));

// =============================================================================
// LOADING FALLBACK
// =============================================================================

const pageLoader = (
  <div className="flex items-center justify-center min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={pageLoader}>
      <Component />
    </Suspense>
  );
}

// =============================================================================
// ROOT ROUTE
// =============================================================================

const rootRoute = createRootRoute({
  component: () => (
    <div>
      <Outlet />
    </div>
  ),
});

// =============================================================================
// PUBLIC ROUTES
// =============================================================================

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <GuestRoute>
      <LandingPage />
    </GuestRoute>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: parseLoginSearch,
  component: () => (
    <GuestRoute>
      <LoginForm />
    </GuestRoute>
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => (
    <GuestRoute>
      <RegisterForm />
    </GuestRoute>
  ),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: () => (
    <GuestRoute>
      <RegisterForm />
    </GuestRoute>
  ),
});

const publicDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  component: PublicDocsPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: () => (
    <GuestRoute>
      <ForgotPasswordForm />
    </GuestRoute>
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: parseResetPasswordSearch,
  component: () => (
    <GuestRoute>
      <ResetPasswordForm />
    </GuestRoute>
  ),
});

// Auth reset password route (for links from email)
const authResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset-password',
  validateSearch: parseResetPasswordSearch,
  component: () => (
    <GuestRoute>
      <ResetPasswordForm />
    </GuestRoute>
  ),
});

// Auth magic-link route (for links from email)
const authMagicLinkVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/magic-link/verify',
  validateSearch: parseMagicLinkVerifySearch,
  component: MagicLinkVerifyPage,
});

// =============================================================================
// DASHBOARD ROUTE
// =============================================================================

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: () => withProtection(<DashboardPage />),
});

const dashboardProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/profile',
  beforeLoad: () => {
    throw redirect({ to: '/settings/profile' });
  },
  component: () => null,
});

const dashboardSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/preferences' });
  },
  component: () => null,
});

// =============================================================================
// HRMS ROUTES - Employees
// =============================================================================

const legalEntitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/companies',
  component: () =>
    withProtection(withSuspense(LegalEntitiesPage), {
      requiredPermission: 'hrms:legal-entity:read:tenant',
    }),
});

const employeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees',
  component: () =>
    withProtection(withSuspense(EmployeesPage), {
      requiredPermission: 'hrms:employee:read:tenant',
    }),
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: () =>
    withProtection(withSuspense(OnboardingPage), {
      requiredPermission: 'hrms:onboarding:read:tenant',
    }),
});

const onboardingProgramTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/program-templates',
  component: () =>
    withProtection(withSuspense(OnboardingProgramTemplatesPage), {
      requiredPermission: 'hrms:onboarding:template:read:tenant',
    }),
});

const documentTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/document-templates',
  component: () =>
    withProtection(withSuspense(DocumentTemplatesPage), {
      requiredPermission: 'hrms:onboarding:template:read:tenant',
    }),
});

const offboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offboarding',
  component: () =>
    withProtection(withSuspense(OffboardingPage), {
      requiredPermission: 'hrms:offboarding:read:tenant',
    }),
});

// My Profile redirect route - for employees to access their own profile
const myProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/me',
  component: () =>
    withProtection(withSuspense(MyProfileRedirect), {
      requiredPermission: 'hrms:employee:read:self',
    }),
});

const myTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/my-team',
  component: () =>
    withProtection(withSuspense(MyTeamPage), {
      requiredPermissions: ['hrms:employee:read:team', 'hrms:employee:read:department'],
      requiredRole: ['people_manager', 'team_lead', 'department_head'],
      requireAny: true,
    }),
});

const employeeCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/new',
  component: () =>
    withProtection(withSuspense(EmployeeCreatePage), {
      requiredPermission: 'hrms:employee:create:tenant',
    }),
});

const employeeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/$id',
  component: () =>
    withProtection(withSuspense(EmployeeDetailPage), {
      requiredPermissions: ['hrms:employee:read:tenant', 'hrms:employee:read:self'],
      requireAny: true,
    }),
});

// =============================================================================
// HRMS ROUTES - Departments
// =============================================================================

const departmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/departments',
  component: () =>
    withProtection(withSuspense(DepartmentsPage), {
      requiredPermission: 'hrms:department:read:tenant',
    }),
});

const departmentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/departments/$id',
  component: () =>
    withProtection(withSuspense(DepartmentDetailPage), {
      requiredPermission: 'hrms:department:read:tenant',
    }),
});

const teamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams',
  component: () =>
    withProtection(withSuspense(TeamsPage), {
      requiredPermission: 'hrms:team:read:tenant',
    }),
});

const designationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/designations',
  component: () =>
    withProtection(withSuspense(DesignationsPage), {
      requiredPermission: 'hrms:designation:read:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Positions
// =============================================================================

const positionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/positions',
  component: () =>
    withProtection(withSuspense(PositionsPage), {
      requiredPermission: 'hrms:position:read:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Locations
// =============================================================================

const locationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/locations',
  component: () =>
    withProtection(withSuspense(LocationsPage), {
      requiredPermission: 'hrms:location:read:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Leaves
// =============================================================================

const leavesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves',
  component: () =>
    withProtection(withSuspense(LeavesPage), {
      requiredPermissions: ['hrms:leave-request:read:tenant', 'hrms:leave-request:read:self'],
      requireAny: true,
    }),
});

const leaveRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/requests',
  component: () =>
    withProtection(withSuspense(LeaveRequestsPage), {
      requiredPermissions: ['hrms:leave-request:read:tenant', 'hrms:leave-request:read:self'],
      requireAny: true,
    }),
});

const leaveCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/calendar',
  component: () =>
    withProtection(withSuspense(LeaveCalendarPage), {
      requiredPermissions: ['hrms:leave-request:read:tenant', 'hrms:leave-request:read:self'],
      requireAny: true,
    }),
});

const leaveBalanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/balance',
  component: () =>
    withProtection(withSuspense(LeaveBalancePage), {
      requiredPermission: 'hrms:leave-balance:read:self',
    }),
});

const leaveTypesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/types',
  component: () =>
    withProtection(withSuspense(LeaveTypesPage), {
      requiredPermission: 'hrms:leave-type:read:tenant',
    }),
});

const leaveTransactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/transactions',
  component: () =>
    withProtection(withSuspense(LeaveTransactionHistoryPage), {
      requiredPermission: 'hrms:leave-balance:read:self',
    }),
});

const leaveEncashmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/encashment',
  component: () =>
    withProtection(withSuspense(LeaveEncashmentPage), {
      requiredPermission: 'hrms:leave-balance:read:self',
    }),
});

const leaveDelegationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/delegations',
  component: () =>
    withProtection(withSuspense(DelegationManagementPage), {
      requiredPermission: 'hrms:approval-delegation:manage:self',
    }),
});

const leaveAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaves/admin',
  component: () =>
    withProtection(withSuspense(LeaveAdminPage), {
      requiredPermission: 'hrms:leave-admin:manage:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Holidays
// =============================================================================

const holidaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/holidays',
  component: () =>
    withProtection(withSuspense(HolidayCalendarPage), {
      requiredPermission: 'hrms:holiday:read:tenant',
    }),
});

const holidayPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/holidays/policy',
  component: () =>
    withProtection(withSuspense(HolidayPolicyPage), {
      requiredPermission: 'hrms:holiday:manage:tenant',
    }),
});

const myHolidaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-holidays',
  component: () =>
    withProtection(withSuspense(MyHolidaysPage), {
      requiredPermission: 'hrms:holiday:read:self',
    }),
});

const floatingHolidayApprovalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/holidays/floating-approvals',
  component: () =>
    withProtection(withSuspense(FloatingHolidayApprovalsPage), {
      requiredPermission: 'hrms:holiday:approve:team',
    }),
});

// =============================================================================
// HRMS ROUTES - Shifts
// =============================================================================

const shiftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shifts',
  component: () =>
    withProtection(withSuspense(ShiftManagementPage), {
      requiredPermissions: ['attendance:shift:read:tenant', 'attendance:shift_schedule:read:self'],
      requireAny: true,
    }),
});

const shiftRosterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shifts/roster',
  component: () =>
    withProtection(withSuspense(ShiftRosterPage), {
      requiredPermissions: ['attendance:shift:read:tenant', 'attendance:shift_schedule:read:self'],
      requireAny: true,
    }),
});

const shiftSwapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shifts/swap',
  component: () =>
    withProtection(withSuspense(ShiftSwapPage), {
      requiredPermissions: ['attendance:shift-swap:read:tenant', 'attendance:shift-swap:read:self'],
      requireAny: true,
    }),
});

const shiftDayRosterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shifts/day-roster',
  validateSearch: (search: Record<string, unknown>) => ({
    date: (search.date as string) || new Date().toISOString().split('T')[0],
  }),
  component: () =>
    withProtection(withSuspense(DayRosterPage), {
      requiredPermissions: ['attendance:shift:read:tenant', 'attendance:shift_schedule:read:self'],
      requireAny: true,
    }),
});

const shiftChangeRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shifts/change-requests',
  component: () =>
    withProtection(withSuspense(ShiftChangeRequestPage), {
      requiredPermissions: [
        'attendance:shift_change:read:self',
        'attendance:shift_change:read:team',
        'attendance:shift_change:read:tenant',
      ],
      requireAny: true,
    }),
});

// =============================================================================
// HRMS ROUTES - Skills & Qualifications
// =============================================================================

const skillCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skills',
  component: () =>
    withProtection(withSuspense(SkillCatalogPage), {
      requiredPermission: 'hrms:skill:read:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Assets
// =============================================================================

const assetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets',
  component: () =>
    withProtection(withSuspense(AssetsPage), { requiredPermission: 'hrms:asset:read:tenant' }),
});

// =============================================================================
// HRMS ROUTES - Benefits
// =============================================================================

const benefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benefits',
  component: () =>
    withProtection(withSuspense(BenefitsPage), {
      requiredPermissions: [
        'hrms:benefits:plan:read:tenant',
        'hrms:benefits:enrollment:read:tenant',
        'hrms:benefits:claim:read:tenant',
        'hrms:benefits:statistics:read:tenant',
      ],
    }),
});

const myBenefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benefits/my',
  component: () =>
    withProtection(withSuspense(MyBenefitsPage), {
      requiredPermissions: [
        'hrms:benefits:plan:read:tenant',
        'hrms:benefits:enrollment:read:self',
        'hrms:benefits:claim:read:self',
      ],
    }),
});

const benefitPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benefits/plans',
  component: () =>
    withProtection(withSuspense(BenefitPlansPage), {
      requiredPermissions: [
        'hrms:benefits:plan:read:tenant',
        'hrms:benefits:plan:create:tenant',
        'hrms:benefits:plan:update:tenant',
      ],
    }),
});

const benefitEnrollmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benefits/enrollments',
  component: () =>
    withProtection(withSuspense(EnrollmentsPage), {
      requiredPermission: 'hrms:benefits:enrollment:read:tenant',
    }),
});

const benefitClaimsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benefits/claims',
  component: () =>
    withProtection(withSuspense(ClaimsPage), {
      requiredPermission: 'hrms:benefits:claim:read:tenant',
    }),
});

// =============================================================================
// HRMS ROUTES - Recruitment
// =============================================================================

const recruitmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recruitment',
  component: () =>
    withProtection(withSuspense(CandidatesPage), {
      requiredPermission: 'hrms:recruitment:read:tenant',
    }),
});

const recruitmentCandidatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recruitment/candidates',
  component: () =>
    withProtection(withSuspense(CandidatesPage), {
      requiredPermission: 'hrms:recruitment:read:tenant',
    }),
});

const recruitmentJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recruitment/jobs',
  component: () =>
    withProtection(withSuspense(JobPostingsPage), {
      requiredPermission: 'hrms:recruitment:read:tenant',
    }),
});

// =============================================================================
// ATTENDANCE ROUTES
// =============================================================================

const attendanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance',
  component: () =>
    withProtection(withSuspense(AttendanceDashboardPage), {
      requiredPermissions: ['attendance:record:read:tenant', 'attendance:record:read:self'],
      requireAny: true,
    }),
});

const attendanceDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/dashboard',
  component: () =>
    withProtection(withSuspense(AttendanceDashboardPage), {
      requiredPermissions: ['attendance:record:read:tenant', 'attendance:record:read:self'],
      requireAny: true,
    }),
});

const timesheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/timesheet',
  component: () =>
    withProtection(withSuspense(TimesheetPage), {
      requiredPermission: 'attendance:timesheet:read:self',
    }),
});

const timesheetApprovalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/timesheet/approvals',
  component: () =>
    withProtection(withSuspense(TimesheetApprovalsPage), {
      requiredPermissions: [
        'attendance:timesheet:approve:team',
        'attendance:timesheet:approve:department',
        'attendance:timesheet:approve:tenant',
      ],
      requireAny: true,
    }),
});

const regularizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/regularization',
  component: () =>
    withProtection(withSuspense(RegularizationPage), {
      requiredPermissions: REGULARIZATION_PAGE_ACCESS_PERMISSIONS,
      requireAny: true,
    }),
});

const violationDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/violations',
  component: () =>
    withProtection(withSuspense(ViolationDashboardPage), {
      requiredPermissions: ['attendance:violation:read:tenant', 'attendance:violation:read:self'],
      requireAny: true,
    }),
});

const teamAttendanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/team-attendance',
  component: () =>
    withProtection(withSuspense(TeamAttendancePage), {
      requiredPermissions: [
        'attendance:record:read:team',
        'attendance:record:read:department',
        'attendance:record:read:tenant',
      ],
      requireAny: true,
    }),
});

const attendanceAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/analytics',
  component: () =>
    withProtection(withSuspense(AnalyticsPage), {
      requiredPermission: 'attendance:analytics:read:tenant',
    }),
});

const attendanceDeviceEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/device-events',
  component: () =>
    withProtection(withSuspense(DeviceEventsPage), {
      requiredPermission: 'attendance:time-event:read:tenant',
    }),
});

const attendanceImportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/imports',
  component: () =>
    withProtection(withSuspense(AttendanceImportsPage), {
      requiredPermissions: ['attendance:bulk:read:tenant', 'attendance:bulk:approve:tenant'],
      requireAny: true,
    }),
});

const attendancePolicyListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/policies',
  component: () =>
    withProtection(withSuspense(PolicyListPage), {
      requiredPermission: 'attendance:policy:read:tenant',
    }),
});

const attendancePolicyCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/policies/create',
  component: () =>
    withProtection(withSuspense(PolicyFormPage), {
      requiredPermission: 'attendance:policy:create:tenant',
    }),
});

const attendancePolicyEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/policies/$policyId/edit',
  component: () =>
    withProtection(withSuspense(PolicyFormPage), {
      requiredPermission: 'attendance:policy:update:tenant',
    }),
});

const overtimeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attendance/overtime',
  component: () =>
    withProtection(withSuspense(OvertimePage), {
      requiredPermissions: OVERTIME_PAGE_ACCESS_PERMISSIONS,
      requireAny: true,
    }),
});

const approvalWorkflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/approval-workflows',
  component: () =>
    withProtection(withSuspense(ApprovalWorkflowsPage), {
      requiredPermission: 'attendance:workflows:manage:tenant',
    }),
});

// =============================================================================
// NOTIFICATIONS ROUTES
// =============================================================================

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: () => withProtection(withSuspense(NotificationsPage)),
});

// =============================================================================
// OTHER HRMS ROUTES
// =============================================================================

// Legacy /payroll redirect -> /hrms/payroll
const payrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll',
  beforeLoad: () => {
    throw redirect({ to: '/hrms/payroll' });
  },
  component: () => null,
});

const payrollRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll',
  component: () =>
    withProtection(withSuspense(PayrollRunsPage), {
      requiredPermissions: ['hrms:payroll-run:read:tenant', 'hrms:payslip:read:self'],
      requireAny: true,
    }),
});

const payrollRunDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/runs/$id',
  component: () =>
    withProtection(withSuspense(PayrollRunDetailPage), {
      requiredPermission: 'hrms:payroll-run:read:tenant',
    }),
});

const payrollAccountingMappingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/accounting',
  component: () =>
    withProtection(withSuspense(PayrollAccountingMappingsPage), {
      requiredPermission: 'hrms:payroll-accounting:read:tenant',
    }),
});

const payrollStatutoryReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/statutory-reports',
  component: () =>
    withProtection(withSuspense(PayrollStatutoryReportsPage), {
      requiredPermission: 'hrms:statutory-files:download:tenant',
    }),
});

const payslipDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/payslips/$id',
  component: () =>
    withProtection(withSuspense(PayslipDetailPage), {
      requiredPermissions: ['hrms:payslip:read:tenant', 'hrms:payslip:read:self'],
      requireAny: true,
    }),
});

const myPayslipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/my-payslips',
  component: () =>
    withProtection(withSuspense(MyPayslipsPage), {
      requiredPermission: 'hrms:payslip:read:self',
    }),
});

const statutoryConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/config',
  component: () =>
    withProtection(withSuspense(StatutoryConfigPage), {
      requiredPermission: 'hrms:statutory-config:read:tenant',
    }),
});

const settlementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/settlements/$id',
  component: () =>
    withProtection(withSuspense(SettlementPage), {
      requiredPermission: 'hrms:settlement:read:tenant',
    }),
});

const taxDeclarationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/tax-declarations',
  component: () =>
    withProtection(withSuspense(TaxDeclarationPage), {
      requiredPermissions: ['hrms:payroll-tax:read:self', 'hrms:payroll-tax:read:tenant'],
      requireAny: true,
    }),
});

const salaryComponentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/components',
  component: () =>
    withProtection(withSuspense(SalaryComponentsPage), {
      requiredPermissions: [
        'hrms:salary-component:read:tenant',
        'hrms:salary-component:create:tenant',
        'hrms:salary-component:update:tenant',
      ],
      requireAny: true,
    }),
});

const salaryStructuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hrms/payroll/salaries',
  component: () =>
    withProtection(withSuspense(SalaryStructuresPage), {
      requiredPermission: 'hrms:salary:read:tenant',
    }),
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: () =>
    withProtection(withSuspense(ReportsPage), {
      requiredPermission: 'hrms:reports:read:tenant',
    }),
});

const organizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organization',
  beforeLoad: () => {
    throw redirect({ to: '/settings/organization/profile' });
  },
  component: () => null,
});

// =============================================================================
// UNIFIED SETTINGS ROUTES
// =============================================================================

// Settings root — redirect to first tab (Profile)
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/profile' });
  },
  component: () => null,
});

// My Account
const settingsProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/profile',
  component: () => withSettingsProtection(withSuspense(ProfilePage)),
});

const settingsPreferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/preferences',
  component: () => withSettingsProtection(withSuspense(PreferencesPage)),
});

const settingsNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  component: () => withSettingsProtection(withSuspense(NotificationPreferencesPage)),
});

// Organization
const settingsOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/organization',
  component: () =>
    withSettingsProtection(<OrganizationSettingsRedirect />, {
      requiredPermissions: [
        'hrms:department:read:tenant',
        'hrms:team:read:tenant',
        'tenant:access:read:tenant',
        'tenant:settings:update:tenant',
      ],
      requireAny: true,
    }),
});

const settingsOrgProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/organization/profile',
  component: () =>
    withSettingsProtection(withSuspense(OrgProfilePage), {
      requiredPermission: 'hrms:department:read:tenant',
    }),
});

const settingsOrgRegionalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/organization/regional',
  component: () =>
    withSettingsProtection(withSuspense(OrgRegionalPage), {
      requiredPermissions: ['tenant:access:read:tenant', 'tenant:settings:update:tenant'],
      requireAny: true,
    }),
});

const settingsOrgDepartmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/organization/departments',
  component: () =>
    withSettingsProtection(withSuspense(OrgDepartmentsPage), {
      requiredPermission: 'hrms:department:read:tenant',
    }),
});

const settingsOrgTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/organization/teams',
  component: () =>
    withSettingsProtection(withSuspense(OrgTeamsPage), {
      requiredPermission: 'hrms:team:read:tenant',
    }),
});

// Users & Security
const settingsUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/users',
  component: () =>
    withSettingsProtection(withSuspense(TenantUsersPage), {
      requiredPermission: 'tenant:users:read:tenant',
    }),
});

const settingsRolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/roles',
  component: () =>
    withSettingsProtection(withSuspense(RolesListPage), {
      requiredPermission: 'abac:role:read:tenant',
    }),
});

const settingsAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/auth',
  component: () =>
    withSettingsProtection(withSuspense(AuthSettingsPage), {
      requiredPermission: 'auth:config:read:tenant',
    }),
});

const settingsAuditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/audit-logs',
  component: () =>
    withSettingsProtection(withSuspense(AuditLogsPage), {
      requiredPermissions: ['auth:audit:read:tenant', 'hrms:audit:read:tenant'],
      requireAny: true,
    }),
});

// Billing
const settingsBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/billing',
  component: () =>
    withSettingsProtection(withSuspense(PlanSettingsPage), {
      requiredPermission: 'tenant:plans:read:tenant',
    }),
});

// System (superadmin only)
const settingsSystemConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/config',
  component: () =>
    withSettingsProtection(withSuspense(SystemConfigPage), { requiredRole: 'superadmin' }),
});

const settingsSystemOperationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/operations',
  component: () =>
    withSettingsProtection(withSuspense(PlatformOperationsPage), { requiredRole: 'superadmin' }),
});

const settingsSystemFeatureFlagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/feature-flags',
  component: () =>
    withSettingsProtection(withSuspense(FeatureFlagsPage), { requiredRole: 'superadmin' }),
});

const settingsSystemPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/plans',
  component: () =>
    withSettingsProtection(withSuspense(PlanCatalogPage), { requiredRole: 'superadmin' }),
});

const settingsSystemTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/tenants',
  component: () =>
    withSettingsProtection(withSuspense(TenantsListPage), { requiredRole: 'superadmin' }),
});

const settingsSystemEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/events',
  component: () =>
    withSettingsProtection(withSuspense(EventGovernancePage), {
      requiredRole: 'superadmin',
      requiredPermission: 'admin:operations:events:read:global',
    }),
});

const settingsSystemTenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/tenants/$tenantId',
  component: () =>
    withSettingsProtection(withSuspense(TenantDetailPage), { requiredRole: 'superadmin' }),
});

// Phase 2.1B.1 — Platform-wide Zepto webhook auth (SuperAdmin only).
// Owns the static-header pair used by the tenantless `/zeptomail-webhook/events`
// route. Rotation flow shows the plaintext value ONCE for paste into Zepto's
// dashboard; 24h grace window preserves in-flight delivery auth.
const settingsSystemZeptoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/system/zepto-platform',
  component: () =>
    withSettingsProtection(withSuspense(ZeptoPlatformConfigPage), { requiredRole: 'superadmin' }),
});

// Legacy settings route (Plan & Billing old path)
const planSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/plan',
  beforeLoad: () => {
    throw redirect({ to: '/settings/billing' });
  },
  component: () => null,
});

// =============================================================================
// CRM ROUTES
// =============================================================================

const crmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm',
  // Workspace-entry gate: routes to the chooser / control tower / straight-in.
  component: () => withProtection(withSuspense(CrmEntryGate), { requiredModule: 'crm' }),
});

// Workspace-entry: the post-login chooser (multi-unit members) and the all-units
// control tower (admins). Both gated on the coarse CRM admission permission.
const crmChooseWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/choose-workspace',
  // `next` = where to continue after the pick (set by the shell gate when it
  // intercepts a login landing or a deep-link). Internal paths only.
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.next === 'string' &&
    search.next.startsWith('/') &&
    !search.next.startsWith('//')
      ? { next: search.next }
      : {}),
  }),
  component: () =>
    withBareProtection(withSuspense(ChooseWorkspacePage), {
      requiredModule: 'crm',
      requiredPermission: 'crm:access:read:tenant',
    }),
});

const crmOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/overview',
  component: () =>
    withProtection(withSuspense(ControlTowerPage), {
      requiredModule: 'crm',
      requiredPermission: 'crm:access:read:tenant',
    }),
});

const crmLeadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/leads',
  component: () =>
    withProtection(withSuspense(LeadsPage), {
      requiredPermissions: [
        'crm:lead:read:self',
        'crm:lead:read:team',
        'crm:lead:read:department',
        'crm:lead:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmLeadDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/leads/$id',
  component: () =>
    withProtection(withSuspense(LeadDetailPage), {
      requiredPermissions: [
        'crm:lead:read:self',
        'crm:lead:read:team',
        'crm:lead:read:department',
        'crm:lead:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmContactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contacts',
  component: () =>
    withProtection(withSuspense(ContactsPage), {
      requiredPermissions: [
        'crm:contact:read:self',
        'crm:contact:read:team',
        'crm:contact:read:department',
        'crm:contact:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmContactDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contacts/$id',
  component: () =>
    withProtection(withSuspense(ContactDetailPage), {
      requiredPermissions: [
        'crm:contact:read:self',
        'crm:contact:read:team',
        'crm:contact:read:department',
        'crm:contact:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/accounts',
  component: () =>
    withProtection(withSuspense(AccountsPage), {
      requiredPermissions: [
        'crm:account:read:self',
        'crm:account:read:team',
        'crm:account:read:department',
        'crm:account:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmAccountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/accounts/$id',
  component: () =>
    withProtection(withSuspense(AccountDetailPage), {
      requiredPermissions: [
        'crm:account:read:self',
        'crm:account:read:team',
        'crm:account:read:department',
        'crm:account:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmDealsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/deals',
  component: () =>
    withProtection(withSuspense(DealsPage), {
      requiredPermissions: [
        'crm:deal:read:self',
        'crm:deal:read:team',
        'crm:deal:read:department',
        'crm:deal:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmDealDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/deals/$id',
  component: () =>
    withProtection(withSuspense(DealDetailPage), {
      requiredPermissions: [
        'crm:deal:read:self',
        'crm:deal:read:team',
        'crm:deal:read:department',
        'crm:deal:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/pipeline',
  component: () =>
    withProtection(withSuspense(PipelineKanban), {
      requiredPermissions: [
        'crm:pipeline:read:self',
        'crm:pipeline:read:team',
        'crm:pipeline:read:department',
        'crm:pipeline:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/tasks',
  component: () =>
    withProtection(withSuspense(TasksPage), {
      requiredPermissions: [
        'crm:task:read:self',
        'crm:task:read:team',
        'crm:task:read:department',
        'crm:task:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmTaskDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/tasks/$id',
  component: () =>
    withProtection(withSuspense(TaskDetailPage), {
      requiredPermissions: [
        'crm:task:read:self',
        'crm:task:read:team',
        'crm:task:read:department',
        'crm:task:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmProductsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/products',
  // Real-estate extension R1 (D16): a project's Structure tab deep-links into
  // the inventory tree at a section; the bare page redirects to Projects.
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.parentId === 'string' && search.parentId
      ? { parentId: search.parentId }
      : {}),
  }),
  component: () =>
    withProtection(withSuspense(ProductsPage), {
      requiredPermissions: [
        'crm:product:read:self',
        'crm:product:read:team',
        'crm:product:read:department',
        'crm:product:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmProductNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/products/new',
  component: () =>
    withProtection(withSuspense(ProductFormPage), {
      requiredPermission: 'crm:product:create:tenant',
    }),
});

const crmProductEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/products/$id/edit',
  component: () =>
    withProtection(withSuspense(ProductFormPage), {
      requiredPermission: 'crm:product:update:tenant',
    }),
});

// ADR 2026-09-05 (Company → Project → Team) S1 — gated on the project
// permission family (sales roles read; the CRM admin creates/updates).
const crmProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/projects',
  component: () =>
    withProtection(withSuspense(ProjectsPage), {
      requiredPermission: 'crm:project:read:tenant',
    }),
});

const crmProjectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/projects/$id',
  // R4: a site visit's unit chip / the unit form deep-link to a grid cell.
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.tab === 'string' && search.tab ? { tab: search.tab } : {}),
    ...(typeof search.sectionId === 'string' && search.sectionId
      ? { sectionId: search.sectionId }
      : {}),
    ...(typeof search.unitId === 'string' && search.unitId ? { unitId: search.unitId } : {}),
  }),
  component: () =>
    withProtection(withSuspense(ProjectDetailPage), {
      requiredPermission: 'crm:project:read:tenant',
    }),
});

const crmCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/campaigns',
  component: () =>
    withProtection(withSuspense(CampaignsPage), {
      requiredPermissions: [
        'crm:campaign:read:self',
        'crm:campaign:read:team',
        'crm:campaign:read:department',
        'crm:campaign:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/analytics',
  component: () =>
    withProtection(withSuspense(SalesAnalyticsPage), {
      requiredPermissions: [
        'crm:analytics:read:self',
        'crm:analytics:read:team',
        'crm:analytics:read:department',
        'crm:analytics:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/reports',
  component: () =>
    withProtection(withSuspense(CRMReportsPage), {
      requiredPermissions: [
        'crm:reports:read:self',
        'crm:reports:read:team',
        'crm:reports:read:department',
        'crm:reports:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmAutomationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/automation',
  component: () =>
    withProtection(withSuspense(WorkflowAutomationPage), {
      requiredPermission: 'crm:workflow:read:tenant',
    }),
});

const crmAutomationNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/automation/new',
  component: () =>
    withProtection(withSuspense(WorkflowEditorPage), {
      requiredPermission: 'crm:workflow:create:tenant',
    }),
});

const crmAutomationEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/automation/$id/edit',
  component: () =>
    withProtection(withSuspense(WorkflowEditorPage), {
      requiredPermission: 'crm:workflow:update:tenant',
    }),
});

const crmJourneysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/journeys',
  component: () =>
    withProtection(withSuspense(JourneyListPage), {
      requiredPermission: 'crm:workflow:read:tenant',
    }),
});

const crmJourneyBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/journeys/$id',
  component: () =>
    withProtection(withSuspense(JourneyBuilderPage), {
      requiredPermission: 'crm:workflow:read:tenant',
    }),
});

const crmNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/notifications',
  component: () =>
    withProtection(withSuspense(NotificationsCenterPage), {
      requiredPermission: 'crm:sse:read:tenant',
    }),
});

const crmEmailTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/email-templates',
  component: () =>
    withProtection(withSuspense(EmailTemplatesPage), {
      requiredPermissions: [
        'crm:email-template:read:self',
        'crm:email-template:read:team',
        'crm:email-template:read:department',
        'crm:email-template:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmPipelineSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/pipelines',
  component: () =>
    withCrmSettingsProtection(withSuspense(PipelineSettingsPage), {
      requiredPermission: 'crm:pipeline:update:tenant',
    }),
});

const crmArchiveSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/archive',
  component: () =>
    withCrmSettingsProtection(withSuspense(ArchiveSettingsPage), {
      requiredPermission: 'crm:archive:manage:tenant',
    }),
});

const crmPaymentTermsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/payment-terms',
  component: () =>
    withCrmSettingsProtection(withSuspense(PaymentTermsSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmPaymentMethodsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/payment-methods',
  component: () =>
    withCrmSettingsProtection(withSuspense(PaymentMethodsSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmTaxSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/tax',
  component: () =>
    withCrmSettingsProtection(withSuspense(TaxSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmExchangeRatesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/exchange-rates',
  component: () =>
    withCrmSettingsProtection(withSuspense(ExchangeRatesSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmPriceBooksSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/price-books',
  component: () =>
    withCrmSettingsProtection(withSuspense(PriceBooksSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmApprovalWorkflowSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/approval-workflows',
  component: () =>
    withCrmSettingsProtection(withSuspense(ApprovalWorkflowSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmIntakeFormsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/intake-forms',
  component: () =>
    withCrmSettingsProtection(withSuspense(IntakeFormsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmArchiveBrowserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/archive',
  component: () =>
    withProtection(withSuspense(ArchiveBrowserPage), {
      requiredPermission: 'crm:archive:read:tenant',
    }),
});

const crmLeadScoringSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/lead-scoring',
  component: () =>
    withCrmSettingsProtection(withSuspense(LeadScoringSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmWebhookSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/webhooks',
  component: () =>
    withCrmSettingsProtection(withSuspense(WebhookSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmIntegrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/integrations',
  component: () =>
    withCrmSettingsProtection(withSuspense(IntegrationsPage), {
      requiredPermission: 'crm:setting:read:tenant',
    }),
});

const crmApprovalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/approvals',
  component: () =>
    withProtection(withSuspense(ApprovalsInboxPage), {
      // Approvers (sales_manager) hold crm:quote:read:tenant — the same gate as
      // the GET /approvals/pending API this page calls. Gating on the admin-only
      // crm:setting:read:tenant locked the designated approver out of her inbox.
      requiredPermission: 'crm:quote:read:tenant',
    }),
});

// Buyer portal — PUBLIC routes (buyers have no staff account; the pages
// authenticate themselves against the public portal API).
const portalLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$tenantSlug',
  component: () => withSuspense(PortalLoginPage),
});
const portalHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$tenantSlug/home',
  component: () => withSuspense(PortalHomePage),
});

const crmPartnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/partners',
  component: () =>
    withProtection(withSuspense(PartnersPage), {
      requiredPermissions: [
        'crm:vendor:read:self',
        'crm:vendor:read:team',
        'crm:vendor:read:department',
        'crm:vendor:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmVendorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/vendors',
  component: () =>
    withProtection(withSuspense(VendorsPage), {
      requiredPermissions: [
        'crm:vendor:read:self',
        'crm:vendor:read:team',
        'crm:vendor:read:department',
        'crm:vendor:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmVendorNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/vendors/new',
  component: () =>
    withProtection(withSuspense(VendorFormPage), {
      requiredPermission: 'crm:vendor:create:tenant',
    }),
});

const crmVendorEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/vendors/$id/edit',
  component: () =>
    withProtection(withSuspense(VendorFormPage), {
      requiredPermissions: [
        'crm:vendor:update:self',
        'crm:vendor:update:team',
        'crm:vendor:update:department',
        'crm:vendor:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmVendorDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/vendors/$id',
  component: () =>
    withProtection(withSuspense(VendorDetailPage), {
      requiredPermissions: [
        'crm:vendor:read:self',
        'crm:vendor:read:team',
        'crm:vendor:read:department',
        'crm:vendor:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmPurchaseOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/purchase-orders',
  component: () =>
    withProtection(withSuspense(PurchaseOrdersPage), {
      requiredPermission: 'crm:purchase-order:read:tenant',
    }),
});

const crmPurchaseOrderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/purchase-orders/new',
  component: () =>
    withProtection(withSuspense(PurchaseOrderFormPage), {
      requiredPermission: 'crm:purchase-order:create:tenant',
    }),
});

const crmPurchaseOrderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/purchase-orders/$id/edit',
  component: () =>
    withProtection(withSuspense(PurchaseOrderFormPage), {
      requiredPermission: 'crm:purchase-order:create:tenant',
    }),
});

const crmPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/purchase-orders/$id',
  component: () =>
    withProtection(withSuspense(PurchaseOrderDetailPage), {
      requiredPermission: 'crm:purchase-order:read:tenant',
    }),
});

const crmQuotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/quotes',
  component: () =>
    withProtection(withSuspense(QuotesPage), {
      requiredPermission: 'crm:quote:read:tenant',
    }),
});

const crmQuoteNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/quotes/new',
  component: () =>
    withProtection(withSuspense(QuoteFormPage), {
      requiredPermission: 'crm:quote:create:tenant',
    }),
});

const crmQuoteEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/quotes/$id/edit',
  component: () =>
    withProtection(withSuspense(QuoteFormPage), {
      requiredPermission: 'crm:quote:update:tenant',
    }),
});

const crmQuoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/quotes/$id',
  component: () =>
    withProtection(withSuspense(QuoteDetailPage), {
      requiredPermission: 'crm:quote:read:tenant',
    }),
});

const crmCasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/cases',
  component: () =>
    withProtection(withSuspense(CasesPage), {
      requiredPermissions: [
        'crm:case:read:self',
        'crm:case:read:team',
        'crm:case:read:department',
        'crm:case:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCaseNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/cases/new',
  component: () =>
    withProtection(withSuspense(CaseFormPage), {
      requiredPermission: 'crm:case:create:tenant',
    }),
});

const crmCaseEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/cases/$id/edit',
  component: () =>
    withProtection(withSuspense(CaseFormPage), {
      requiredPermissions: [
        'crm:case:update:self',
        'crm:case:update:team',
        'crm:case:update:department',
        'crm:case:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmCaseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/cases/$id',
  component: () =>
    withProtection(withSuspense(CaseDetailPage), {
      requiredPermissions: [
        'crm:case:read:self',
        'crm:case:read:team',
        'crm:case:read:department',
        'crm:case:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCampaignNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/campaigns/new',
  component: () =>
    withProtection(withSuspense(CampaignFormPage), {
      requiredPermission: 'crm:campaign:create:tenant',
    }),
});

const crmCampaignEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/campaigns/$id/edit',
  component: () =>
    withProtection(withSuspense(CampaignFormPage), {
      requiredPermissions: [
        'crm:campaign:update:self',
        'crm:campaign:update:team',
        'crm:campaign:update:department',
        'crm:campaign:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmCampaignDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/campaigns/$id',
  component: () =>
    withProtection(withSuspense(CampaignDetailPage), {
      requiredPermissions: [
        'crm:campaign:read:self',
        'crm:campaign:read:team',
        'crm:campaign:read:department',
        'crm:campaign:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmDuplicatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/duplicates',
  component: () =>
    withProtection(withSuspense(DuplicatesPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmContractsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contracts',
  component: () =>
    withProtection(withSuspense(ContractsPage), {
      requiredPermissions: [
        'crm:contract:read:self',
        'crm:contract:read:team',
        'crm:contract:read:department',
        'crm:contract:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmContractNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contracts/new',
  component: () =>
    withProtection(withSuspense(ContractFormPage), {
      requiredPermission: 'crm:contract:create:tenant',
    }),
});

const crmContractEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contracts/$id/edit',
  component: () =>
    withProtection(withSuspense(ContractFormPage), {
      requiredPermissions: [
        'crm:contract:update:self',
        'crm:contract:update:team',
        'crm:contract:update:department',
        'crm:contract:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmContractDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/contracts/$id',
  component: () =>
    withProtection(withSuspense(ContractDetailPage), {
      requiredPermissions: [
        'crm:contract:read:self',
        'crm:contract:read:team',
        'crm:contract:read:department',
        'crm:contract:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmActivitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/activities',
  component: () =>
    withProtection(withSuspense(ActivitiesPage), {
      requiredPermission: 'crm:activity:read:tenant',
    }),
});

const crmCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/calendar',
  component: () =>
    withProtection(withSuspense(CalendarPage), {
      requiredPermission: 'crm:activity:read:tenant',
    }),
});

const crmSalesOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/sales-orders',
  component: () =>
    withProtection(withSuspense(SalesOrdersPage), {
      requiredPermissions: [
        'crm:sales-order:read:self',
        'crm:sales-order:read:team',
        'crm:sales-order:read:department',
        'crm:sales-order:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmSalesOrderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/sales-orders/new',
  component: () =>
    withProtection(withSuspense(SalesOrderFormPage), {
      requiredPermission: 'crm:sales-order:create:tenant',
    }),
});

const crmSalesOrderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/sales-orders/$id/edit',
  component: () =>
    withProtection(withSuspense(SalesOrderFormPage), {
      requiredPermissions: [
        'crm:sales-order:update:self',
        'crm:sales-order:update:team',
        'crm:sales-order:update:department',
        'crm:sales-order:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmSalesOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/sales-orders/$id',
  component: () =>
    withProtection(withSuspense(SalesOrderDetailPage), {
      requiredPermissions: [
        'crm:sales-order:read:self',
        'crm:sales-order:read:team',
        'crm:sales-order:read:department',
        'crm:sales-order:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmInvoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/invoices',
  component: () =>
    withProtection(withSuspense(InvoicesPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmInvoiceNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/invoices/new',
  component: () =>
    withProtection(withSuspense(InvoiceFormPage), {
      requiredPermission: 'crm:invoice:create:tenant',
    }),
});

const crmInvoiceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/invoices/$id/edit',
  component: () =>
    withProtection(withSuspense(InvoiceFormPage), {
      requiredPermissions: [
        'crm:invoice:update:self',
        'crm:invoice:update:team',
        'crm:invoice:update:department',
        'crm:invoice:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmInvoiceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/invoices/$id',
  component: () =>
    withProtection(withSuspense(InvoiceDetailPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmPaymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/payments',
  component: () =>
    withProtection(withSuspense(PaymentsPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmPaymentNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/payments/new',
  component: () =>
    withProtection(withSuspense(RecordPaymentPage), {
      requiredPermissions: [
        'crm:invoice:update:self',
        'crm:invoice:update:team',
        'crm:invoice:update:department',
        'crm:invoice:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmPaymentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/payments/$id',
  component: () =>
    withProtection(withSuspense(PaymentDetailPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCreditNotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/credit-notes',
  component: () =>
    withProtection(withSuspense(CreditNotesPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCreditNoteNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/credit-notes/new',
  component: () =>
    withProtection(withSuspense(CreditNoteFormPage), {
      requiredPermissions: [
        'crm:invoice:update:self',
        'crm:invoice:update:team',
        'crm:invoice:update:department',
        'crm:invoice:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmCreditNoteEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/credit-notes/$id/edit',
  component: () =>
    withProtection(withSuspense(CreditNoteFormPage), {
      requiredPermissions: [
        'crm:invoice:update:self',
        'crm:invoice:update:team',
        'crm:invoice:update:department',
        'crm:invoice:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmCreditNoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/credit-notes/$id',
  component: () =>
    withProtection(withSuspense(CreditNoteDetailPage), {
      requiredPermissions: [
        'crm:invoice:read:self',
        'crm:invoice:read:team',
        'crm:invoice:read:department',
        'crm:invoice:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmSlaSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/sla',
  component: () =>
    withCrmSettingsProtection(withSuspense(SLASettingsPage), {
      requiredPermissions: [
        'crm:sla:read:self',
        'crm:sla:read:team',
        'crm:sla:read:department',
        'crm:sla:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmGeneralSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/general',
  component: () =>
    withCrmSettingsProtection(withSuspense(GeneralSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

const crmCustomFieldsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/custom-fields',
  component: () =>
    withCrmSettingsProtection(withSuspense(CustomFieldsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

// Inventory M3a (v1.4 FR-11): per-line lifecycle status editor.
const crmInventoryStatusesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/inventory-statuses',
  component: () =>
    withCrmSettingsProtection(withSuspense(InventoryStatusesPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

// Transaction layer: per-line charge editor.
const crmChargesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/charges',
  component: () =>
    withCrmSettingsProtection(withSuspense(ChargesSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

// Transaction layer: per-line payment plan editor.
const crmPaymentPlansSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/payment-plans',
  component: () =>
    withCrmSettingsProtection(withSuspense(PaymentPlansSettingsPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

// Inventory M6 (v1.4 FR-35): Business Line setup wizard + pack gallery.
const crmBusinessLinesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/business-lines',
  component: () =>
    withCrmSettingsProtection(withSuspense(BusinessLinesPage), {
      requiredPermission: 'crm:setting:update:tenant',
    }),
});

// BU-v2 Phase 3 (P3-2): subsidiary management — crm_admin surface, gated on
// the business-unit permission family (never crm:setting — immune to role
// reshuffles like #383).
const crmBusinessUnitsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/settings/business-units',
  component: () =>
    withCrmSettingsProtection(withSuspense(BusinessUnitsPage), {
      requiredPermission: 'crm:business-unit:read:tenant',
    }),
});

const crmForecastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/forecasts',
  component: () =>
    withProtection(withSuspense(ForecastsPage), {
      requiredPermissions: [
        'crm:forecast:read:self',
        'crm:forecast:read:team',
        'crm:forecast:read:department',
        'crm:forecast:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmTerritoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/territories',
  component: () =>
    withProtection(withSuspense(TerritoriesPage), {
      requiredPermissions: [
        'crm:territory:read:self',
        'crm:territory:read:team',
        'crm:territory:read:department',
        'crm:territory:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmTerritoryNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/territories/new',
  component: () =>
    withProtection(withSuspense(TerritoryFormPage), {
      requiredPermission: 'crm:territory:create:tenant',
    }),
});

const crmTerritoryEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/territories/$id/edit',
  component: () =>
    withProtection(withSuspense(TerritoryFormPage), {
      requiredPermission: 'crm:territory:update:tenant',
    }),
});

const crmTerritoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/territories/$id',
  component: () =>
    withProtection(withSuspense(TerritoryDetailPage), {
      requiredPermissions: [
        'crm:territory:read:self',
        'crm:territory:read:team',
        'crm:territory:read:department',
        'crm:territory:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCompetitorAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/competitors/analytics',
  component: () =>
    withProtection(withSuspense(CompetitorAnalyticsPage), {
      requiredPermissions: [
        'crm:competitor:read:self',
        'crm:competitor:read:team',
        'crm:competitor:read:department',
        'crm:competitor:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmCompetitorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/competitors',
  component: () =>
    withProtection(withSuspense(CompetitorsPage), {
      requiredPermissions: [
        'crm:competitor:read:self',
        'crm:competitor:read:team',
        'crm:competitor:read:department',
        'crm:competitor:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmSiteVisitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/site-visits',
  component: () =>
    withProtection(withSuspense(SiteVisitsPage), {
      requiredPermissions: [
        'crm:site-visit:read:self',
        'crm:site-visit:read:team',
        'crm:site-visit:read:department',
        'crm:site-visit:read:tenant',
      ],
      requireAny: true,
    }),
});

const crmSiteVisitNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/site-visits/new',
  // R4: a project's "Plan a visit" pre-anchors the form on the project.
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.entityType === 'string' && search.entityType
      ? { entityType: search.entityType }
      : {}),
    ...(typeof search.entityId === 'string' && search.entityId
      ? { entityId: search.entityId }
      : {}),
    ...(typeof search.projectId === 'string' && search.projectId
      ? { projectId: search.projectId }
      : {}),
  }),
  component: () =>
    withProtection(withSuspense(SiteVisitFormPage), {
      requiredPermission: 'crm:site-visit:create:tenant',
    }),
});

const crmSiteVisitEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/site-visits/$id/edit',
  component: () =>
    withProtection(withSuspense(SiteVisitFormPage), {
      requiredPermissions: [
        'crm:site-visit:update:self',
        'crm:site-visit:update:team',
        'crm:site-visit:update:department',
        'crm:site-visit:update:tenant',
      ],
      requireAny: true,
    }),
});

const crmSiteVisitDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/site-visits/$id',
  component: () =>
    withProtection(withSuspense(SiteVisitDetailPage), {
      requiredPermissions: [
        'crm:site-visit:read:self',
        'crm:site-visit:read:team',
        'crm:site-visit:read:department',
        'crm:site-visit:read:tenant',
      ],
      requireAny: true,
    }),
});

// =============================================================================
// ADMIN ROUTES
// =============================================================================

// Admin routes — redirect to unified settings
const adminTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants',
  beforeLoad: () => {
    throw redirect({ to: '/settings/system/tenants' });
  },
  component: () => null,
});

const adminTenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/$tenantId',
  component: () => withProtection(withSuspense(TenantDetailPage), { requiredRole: 'superadmin' }),
});

const adminTenantConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/$tenantId/config',
  component: () =>
    withProtection(withSuspense(TenantConfigurationPage), { requiredRole: 'superadmin' }),
});

const adminFeatureFlagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/feature-flags',
  beforeLoad: () => {
    throw redirect({ to: '/settings/system/feature-flags' });
  },
  component: () => null,
});

const adminPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/plans',
  beforeLoad: () => {
    throw redirect({ to: '/settings/system/plans' });
  },
  component: () => null,
});

const adminAuditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit-logs',
  beforeLoad: () => {
    throw redirect({ to: '/settings/audit-logs' });
  },
  component: () => null,
});

const adminSystemConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/system-config',
  beforeLoad: () => {
    throw redirect({ to: '/settings/system/config' });
  },
  component: () => null,
});

const adminRolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles',
  beforeLoad: () => {
    throw redirect({ to: '/settings/roles' });
  },
  component: () => null,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  beforeLoad: () => {
    throw redirect({ to: '/settings/users' });
  },
  component: () => null,
});

const adminAuthSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/auth-settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/auth' });
  },
  component: () => null,
});

// Legacy superadmin routes
const superadminTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/superadmin/tenants',
  component: () =>
    withProtection(withSuspense(TenantsOverviewPage), { requiredRole: 'superadmin' }),
});

const superadminTenantAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/superadmin/tenants/$tenantId/auth',
  component: () =>
    withProtection(withSuspense(TenantAuthConfigPage), { requiredRole: 'superadmin' }),
});

// =============================================================================
// ERROR ROUTES
// =============================================================================

const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unauthorized',
  component: UnauthorizedRouteComponent,
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundRouteComponent,
});

// =============================================================================
// ROUTE TREE
// =============================================================================

const routeTree = rootRoute.addChildren([
  // Public routes
  indexRoute,
  loginRoute,
  registerRoute,
  signupRoute,
  publicDocsRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authResetPasswordRoute,
  authMagicLinkVerifyRoute,

  // Dashboard
  dashboardRoute,
  dashboardProfileRoute,
  dashboardSettingsRoute,

  // HRMS - Employees
  legalEntitiesRoute,
  employeesRoute,
  onboardingRoute,
  onboardingProgramTemplatesRoute,
  documentTemplatesRoute,
  offboardingRoute,
  myProfileRoute, // Must come before employeeDetailRoute for /employees/me to work
  myTeamRoute, // Must come before employeeDetailRoute for /employees/my-team to work
  employeeCreateRoute, // Must come before employeeDetailRoute for /employees/new to work
  employeeDetailRoute,

  // HRMS - Departments
  departmentsRoute,
  departmentDetailRoute,
  teamsRoute,

  // HRMS - Designations
  designationsRoute,

  // HRMS - Positions
  positionsRoute,

  // HRMS - Locations
  locationsRoute,

  // HRMS - Leaves
  leavesRoute,
  leaveRequestsRoute,
  leaveCalendarRoute,
  leaveBalanceRoute,
  leaveTypesRoute,
  leaveTransactionsRoute,
  leaveEncashmentRoute,
  leaveDelegationsRoute,
  leaveAdminRoute,

  // HRMS - Holidays
  holidaysRoute,
  holidayPolicyRoute,
  myHolidaysRoute,
  floatingHolidayApprovalsRoute,

  // HRMS - Shifts
  shiftsRoute,
  shiftRosterRoute,
  shiftSwapRoute,
  shiftDayRosterRoute,
  shiftChangeRequestRoute,

  // HRMS - Skills & Qualifications
  skillCatalogRoute,

  // HRMS - Assets (unified page with tabs)
  assetsRoute,

  // HRMS - Benefits
  benefitsRoute,
  myBenefitsRoute,
  benefitPlansRoute,
  benefitEnrollmentsRoute,
  benefitClaimsRoute,

  // HRMS - Recruitment
  recruitmentRoute,
  recruitmentCandidatesRoute,
  recruitmentJobsRoute,

  // Attendance
  attendanceRoute,
  attendanceDashboardRoute,
  timesheetRoute,
  timesheetApprovalsRoute,
  teamAttendanceRoute,
  regularizationRoute,
  violationDashboardRoute,
  attendanceAnalyticsRoute,
  attendanceDeviceEventsRoute,
  attendanceImportsRoute,
  attendancePolicyListRoute,
  attendancePolicyCreateRoute,
  attendancePolicyEditRoute,
  overtimeRoute,
  approvalWorkflowsRoute,

  // Notifications
  notificationsRoute,

  // Payroll
  payrollRoute, // legacy redirect /payroll -> /hrms/payroll
  payrollRunsRoute,
  payrollRunDetailRoute,
  payrollAccountingMappingsRoute,
  payrollStatutoryReportsRoute,
  payslipDetailRoute,
  myPayslipsRoute,
  statutoryConfigRoute,
  settlementRoute,
  taxDeclarationRoute,
  salaryComponentsRoute,
  salaryStructuresRoute,

  // Other HRMS
  reportsRoute,
  organizationRoute, // redirect → /settings/organization/profile

  // Unified Settings
  settingsRoute,
  settingsProfileRoute,
  settingsPreferencesRoute,
  settingsNotificationsRoute,
  settingsOrgRoute,
  settingsOrgProfileRoute,
  settingsOrgRegionalRoute,
  settingsOrgDepartmentsRoute,
  settingsOrgTeamsRoute,
  settingsUsersRoute,
  settingsRolesRoute,
  settingsAuthRoute,
  settingsAuditLogsRoute,
  settingsBillingRoute,
  settingsSystemConfigRoute,
  settingsSystemOperationsRoute,
  settingsSystemFeatureFlagsRoute,
  settingsSystemPlansRoute,
  settingsSystemTenantsRoute,
  settingsSystemEventsRoute,
  settingsSystemTenantDetailRoute,
  settingsSystemZeptoRoute,
  planSettingsRoute, // redirect → /settings/billing

  // CRM routes
  crmRoute,
  crmChooseWorkspaceRoute,
  crmOverviewRoute,
  crmLeadsRoute,
  crmLeadDetailRoute,
  crmContactsRoute,
  crmContactDetailRoute,
  crmAccountsRoute,
  crmAccountDetailRoute,
  crmDealsRoute,
  crmDealDetailRoute,
  crmPipelineRoute,
  crmTasksRoute,
  crmTaskDetailRoute,
  crmProductsRoute,
  crmProductNewRoute,
  crmProductEditRoute,
  crmProjectsRoute,
  crmProjectDetailRoute,
  crmCampaignsRoute,
  crmCampaignNewRoute,
  crmCampaignEditRoute,
  crmAnalyticsRoute,
  crmReportsRoute,
  crmAutomationRoute,
  crmAutomationNewRoute,
  crmAutomationEditRoute,
  crmJourneysRoute,
  crmJourneyBuilderRoute,
  crmNotificationsRoute,
  crmEmailTemplatesRoute,
  crmPipelineSettingsRoute,
  crmArchiveSettingsRoute,
  crmPaymentTermsSettingsRoute,
  crmPaymentMethodsSettingsRoute,
  crmTaxSettingsRoute,
  crmExchangeRatesSettingsRoute,
  crmPriceBooksSettingsRoute,
  crmApprovalWorkflowSettingsRoute,
  crmIntakeFormsRoute,
  crmArchiveBrowserRoute,
  crmLeadScoringSettingsRoute,
  crmWebhookSettingsRoute,
  crmIntegrationsRoute,
  crmApprovalsRoute,
  portalLoginRoute,
  portalHomeRoute,
  crmPartnersRoute,
  crmVendorsRoute,
  crmVendorNewRoute,
  crmVendorEditRoute,
  crmVendorDetailRoute,
  crmPurchaseOrdersRoute,
  crmPurchaseOrderNewRoute,
  crmPurchaseOrderEditRoute,
  crmPurchaseOrderDetailRoute,
  crmQuotesRoute,
  crmQuoteNewRoute,
  crmQuoteEditRoute,
  crmQuoteDetailRoute,
  crmCasesRoute,
  crmCaseNewRoute,
  crmCaseEditRoute,
  crmCaseDetailRoute,
  crmCampaignDetailRoute,
  crmDuplicatesRoute,
  crmContractsRoute,
  crmContractNewRoute,
  crmContractEditRoute,
  crmContractDetailRoute,
  crmActivitiesRoute,
  crmCalendarRoute,
  crmSalesOrdersRoute,
  crmSalesOrderNewRoute,
  crmSalesOrderEditRoute,
  crmSalesOrderDetailRoute,
  crmInvoicesRoute,
  crmInvoiceNewRoute,
  crmInvoiceEditRoute,
  crmInvoiceDetailRoute,
  crmPaymentsRoute,
  crmPaymentNewRoute,
  crmPaymentDetailRoute,
  crmCreditNotesRoute,
  crmCreditNoteNewRoute,
  crmCreditNoteEditRoute,
  crmCreditNoteDetailRoute,
  crmSlaSettingsRoute,
  crmGeneralSettingsRoute,
  crmCustomFieldsSettingsRoute,
  crmInventoryStatusesSettingsRoute,
  crmChargesSettingsRoute,
  crmPaymentPlansSettingsRoute,
  crmBusinessLinesSettingsRoute,
  crmBusinessUnitsSettingsRoute,
  crmForecastsRoute,
  crmTerritoriesRoute,
  crmTerritoryNewRoute,
  crmTerritoryEditRoute,
  crmTerritoryDetailRoute,
  crmCompetitorAnalyticsRoute,
  crmCompetitorsRoute,
  crmSiteVisitsRoute,
  crmSiteVisitNewRoute,
  crmSiteVisitEditRoute,
  crmSiteVisitDetailRoute,

  // Admin routes
  adminTenantsRoute,
  adminTenantDetailRoute,
  adminTenantConfigRoute,
  adminFeatureFlagsRoute,
  adminPlansRoute,
  adminAuditLogsRoute,
  adminSystemConfigRoute,
  adminRolesRoute,
  adminUsersRoute,
  adminAuthSettingsRoute,

  // Legacy SuperAdmin routes
  superadminTenantsRoute,
  superadminTenantAuthRoute,

  // Error routes
  unauthorizedRoute,
  notFoundRoute,
]);

// =============================================================================
// ROUTER EXPORT
// =============================================================================

export const router = createRouter({
  routeTree,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
